import React, { useState, useEffect, useRef } from 'react';
import { clientsAPI, jobsAPI, usersAPI, reportsAPI, emailSettingsAPI } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { Download, Loader2, FileText, Calendar, Filter, Users, Building2, Mail, Send, Paperclip, X, Upload } from 'lucide-react';

const ReportsPage = () => {
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingClient, setDownloadingClient] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [resources, setResources] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  
  // Report parameters for Internal Report
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [filterType, setFilterType] = useState('');
  const [filterId, setFilterId] = useState('');
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Report parameters for Client Summary
  const [clientYear, setClientYear] = useState(new Date().getFullYear());
  const [clientMonth, setClientMonth] = useState(new Date().getMonth() + 1);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [useClientCustomRange, setUseClientCustomRange] = useState(false);
  const [clientStartDate, setClientStartDate] = useState('');
  const [clientEndDate, setClientEndDate] = useState('');
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const emailFileInputRef = useRef(null);
  const [emailForm, setEmailForm] = useState({
    recipient_email: '',
    recipient_name: '',
    template_id: '',
    custom_message: '',
    subject: ''
  });

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = [2024, 2025, 2026, 2027];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, jobsRes, resourcesRes, templatesRes] = await Promise.all([
        clientsAPI.getAll(),
        jobsAPI.getAll(),
        usersAPI.getSubcontractors(),
        emailSettingsAPI.getTemplates()
      ]);
      setClients(clientsRes.data);
      setJobs(jobsRes.data);
      // Sort resources alphabetically
      const sortedResources = resourcesRes.data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setResources(sortedResources);
      setEmailTemplates(templatesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterTypeChange = (type) => {
    setFilterType(type);
    setFilterId('');
  };

  const getFilterOptions = () => {
    switch (filterType) {
      case 'resource':
        return [...resources].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(r => ({ id: r.id, label: r.name }));
      case 'client':
        return [...clients].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => ({ id: c.id, label: c.name }));
      case 'job':
        return [...jobs].sort((a, b) => (`${a.job_number} - ${a.name}`).localeCompare(`${b.job_number} - ${b.name}`)).map(j => ({ id: j.id, label: `${j.job_number} - ${j.name}` }));
      default:
        return [];
    }
  };

  const handleDownload = async () => {
    // Validate custom date range if enabled
    if (useCustomRange) {
      if (!startDate || !endDate) {
        alert('Please select both start and end dates');
        return;
      }
      if (startDate > endDate) {
        alert('Start date must be before end date');
        return;
      }
    }
    
    setDownloading(true);
    try {
      const response = await reportsAPI.downloadMonthlyReportPDF(
        year,
        month,
        filterType || undefined,
        filterId || undefined,
        useCustomRange ? startDate : undefined,
        useCustomRange ? endDate : undefined
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      let filename;
      if (useCustomRange) {
        filename = `monthly_report_${startDate}_to_${endDate}`;
      } else {
        const monthName = months.find(m => m.value === month)?.label || month;
        filename = `monthly_report_${year}_${monthName}`;
      }
      if (filterType) {
        const filterLabel = getFilterOptions().find(o => o.id === filterId)?.label || filterType;
        filename += `_${filterType}_${filterLabel.replace(/[^a-zA-Z0-9]/g, '_')}`;
      }
      link.download = `${filename}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadClientSummary = async () => {
    if (!selectedClientId) {
      alert('Please select a client');
      return;
    }
    
    // Validate custom date range if enabled
    if (useClientCustomRange) {
      if (!clientStartDate || !clientEndDate) {
        alert('Please select both start and end dates');
        return;
      }
      if (clientStartDate > clientEndDate) {
        alert('Start date must be before end date');
        return;
      }
    }
    
    setDownloadingClient(true);
    try {
      const response = await reportsAPI.downloadClientSummaryPDF(
        clientYear,
        clientMonth,
        selectedClientId,
        selectedProjectId || undefined,
        useClientCustomRange ? clientStartDate : undefined,
        useClientCustomRange ? clientEndDate : undefined
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const clientName = clients.find(c => c.id === selectedClientId)?.name || 'client';
      let filename;
      if (useClientCustomRange) {
        filename = `client_summary_${clientName.replace(/[^a-zA-Z0-9]/g, '_')}_${clientStartDate}_to_${clientEndDate}`;
      } else {
        const monthName = months.find(m => m.value === clientMonth)?.label || clientMonth;
        filename = `client_summary_${clientName.replace(/[^a-zA-Z0-9]/g, '_')}_${clientYear}_${monthName}`;
      }
      link.download = `${filename}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download client summary:', error);
      alert('Failed to generate client summary. Please try again.');
    } finally {
      setDownloadingClient(false);
    }
  };

  const openEmailModal = () => {
    if (!selectedClientId) {
      alert('Please select a client first');
      return;
    }
    
    const client = clients.find(c => c.id === selectedClientId);
    const defaultTemplate = emailTemplates.find(t => t.is_default);
    
    setEmailForm({
      recipient_email: client?.contact_email || '',
      recipient_name: client?.contact_name || client?.name || '',
      template_id: defaultTemplate?.id || '',
      custom_message: '',
      subject: ''
    });
    setEmailAttachments([]);
    setShowEmailModal(true);
  };

  const handleEmailFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    for (const file of files) {
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }
      setEmailAttachments(prev => [...prev, file]);
    }
    
    if (emailFileInputRef.current) {
      emailFileInputRef.current.value = '';
    }
  };

  const removeEmailAttachment = (index) => {
    setEmailAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    
    if (!emailForm.recipient_email) {
      alert('Please enter a recipient email');
      return;
    }
    
    setSendingEmail(true);
    try {
      // For now, we'll send without additional attachments
      // Full attachment support would require FormData handling on the backend
      await reportsAPI.sendClientSummaryEmail(
        clientYear,
        clientMonth,
        selectedClientId,
        emailForm,
        selectedProjectId || undefined
      );
      
      alert('Report sent successfully!');
      setShowEmailModal(false);
      setEmailAttachments([]);
    } catch (error) {
      console.error('Failed to send email:', error);
      alert(error.response?.data?.detail || 'Failed to send email. Please check your email settings.');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#194eb2]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in max-w-4xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Reports</h1>
          <p className="mt-1 text-slate-500">Generate and download PDF reports</p>
        </div>

        {/* Monthly Report Generator */}
        <div className="card-base">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#194eb2] rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Monthly Report</h2>
                <p className="text-sm text-slate-500">Generate a monthly timesheet report as PDF</p>
              </div>
            </div>
          </div>
          <div className="card-body space-y-6">
            {/* Period Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Select Period
              </label>
              
              {/* Period Type Toggle */}
              <div className="flex items-center gap-4 mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="periodType"
                    checked={!useCustomRange}
                    onChange={() => setUseCustomRange(false)}
                    className="text-blue-600"
                  />
                  <span className="text-sm text-slate-600">Calendar Month</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="periodType"
                    checked={useCustomRange}
                    onChange={() => setUseCustomRange(true)}
                    className="text-blue-600"
                  />
                  <span className="text-sm text-slate-600">Custom Date Range</span>
                </label>
              </div>
              
              {!useCustomRange ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Month</label>
                    <select
                      value={month}
                      onChange={(e) => setMonth(parseInt(e.target.value))}
                      className="input-base"
                      data-testid="report-month"
                    >
                      {months.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Year</label>
                    <select
                      value={year}
                      onChange={(e) => setYear(parseInt(e.target.value))}
                      className="input-base"
                      data-testid="report-year"
                    >
                      {years.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="input-base"
                      data-testid="report-start-date"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="input-base"
                      data-testid="report-end-date"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Filter Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filter By (Optional)
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Filter Type</label>
                  <select
                    value={filterType}
                    onChange={(e) => handleFilterTypeChange(e.target.value)}
                    className="input-base"
                    data-testid="filter-type"
                  >
                    <option value="">All (No Filter)</option>
                    <option value="resource">By Resource</option>
                    <option value="client">By Client</option>
                    <option value="job">By Project</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    {filterType === 'resource' ? 'Select Resource' :
                     filterType === 'client' ? 'Select Client' :
                     filterType === 'job' ? 'Select Project' : 'Select Value'}
                  </label>
                  <select
                    value={filterId}
                    onChange={(e) => setFilterId(e.target.value)}
                    className="input-base"
                    disabled={!filterType}
                    data-testid="filter-id"
                  >
                    <option value="">
                      {filterType ? `All ${filterType}s` : 'Select filter type first'}
                    </option>
                    {getFilterOptions().map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Report Preview Info */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="text-sm font-medium text-slate-700 mb-2">Report Preview</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>
                  <span className="text-slate-500">Period:</span>{' '}
                  <span className="font-medium">
                    {useCustomRange 
                      ? (startDate && endDate 
                          ? `${new Date(startDate).toLocaleDateString('en-AU')} - ${new Date(endDate).toLocaleDateString('en-AU')}`
                          : 'Select dates')
                      : `${months.find(m => m.value === month)?.label} ${year}`
                    }
                  </span>
                </li>
                <li>
                  <span className="text-slate-500">Filter:</span>{' '}
                  <span className="font-medium">
                    {filterType 
                      ? `${filterType.charAt(0).toUpperCase() + filterType.slice(1)}: ${
                          filterId 
                            ? getFilterOptions().find(o => o.id === filterId)?.label 
                            : `All ${filterType}s`
                        }`
                      : 'All timesheets (no filter)'
                    }
                  </span>
                </li>
                <li>
                  <span className="text-slate-500">Content:</span>{' '}
                  <span className="font-medium">Approved timesheets with financial summary</span>
                </li>
              </ul>
            </div>

            {/* Download Button */}
            <div className="flex justify-end">
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="btn-primary flex items-center gap-2 px-6"
                data-testid="generate-report-btn"
              >
                {downloading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Generate & Download PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Client Summary Report Generator */}
        <div className="card-base">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Client Summary Report</h2>
                <p className="text-sm text-slate-500">Generate a client-friendly timesheet summary (no financial data)</p>
              </div>
            </div>
          </div>
          <div className="card-body space-y-6">
            {/* Period Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Select Period
              </label>
              
              {/* Period Type Toggle */}
              <div className="flex items-center gap-4 mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="clientPeriodType"
                    checked={!useClientCustomRange}
                    onChange={() => setUseClientCustomRange(false)}
                    className="text-emerald-600"
                  />
                  <span className="text-sm text-slate-600">Calendar Month</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="clientPeriodType"
                    checked={useClientCustomRange}
                    onChange={() => setUseClientCustomRange(true)}
                    className="text-emerald-600"
                  />
                  <span className="text-sm text-slate-600">Custom Date Range</span>
                </label>
              </div>
              
              {!useClientCustomRange ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Month</label>
                    <select
                      value={clientMonth}
                      onChange={(e) => setClientMonth(parseInt(e.target.value))}
                      className="input-base"
                      data-testid="client-report-month"
                    >
                      {months.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Year</label>
                    <select
                      value={clientYear}
                      onChange={(e) => setClientYear(parseInt(e.target.value))}
                      className="input-base"
                      data-testid="client-report-year"
                    >
                      {years.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={clientStartDate}
                      onChange={(e) => setClientStartDate(e.target.value)}
                      className="input-base"
                      data-testid="client-report-start-date"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">End Date</label>
                    <input
                      type="date"
                      value={clientEndDate}
                      onChange={(e) => setClientEndDate(e.target.value)}
                      className="input-base"
                      data-testid="client-report-end-date"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Client Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Select Client
              </label>
              <select
                value={selectedClientId}
                onChange={(e) => {
                  setSelectedClientId(e.target.value);
                  setSelectedProjectId(''); // Reset project when client changes
                }}
                className="input-base"
                data-testid="client-select"
              >
                <option value="">Select a client...</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>

            {/* Project Filter (Optional) */}
            {selectedClientId && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filter by Project (Optional)
                </label>
                <select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  className="input-base"
                  data-testid="project-filter-select"
                >
                  <option value="">All Projects</option>
                  {jobs
                    .filter(j => j.client_id === selectedClientId)
                    .sort((a, b) => `${a.job_number}`.localeCompare(`${b.job_number}`))
                    .map((job) => (
                      <option key={job.id} value={job.id}>{job.job_number} - {job.name}</option>
                    ))}
                </select>
              </div>
            )}

            {/* Report Preview Info */}
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
              <h4 className="text-sm font-medium text-slate-700 mb-2">Report Preview</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>
                  <span className="text-slate-500">Period:</span>{' '}
                  <span className="font-medium">
                    {useClientCustomRange 
                      ? (clientStartDate && clientEndDate 
                          ? `${new Date(clientStartDate).toLocaleDateString('en-AU')} - ${new Date(clientEndDate).toLocaleDateString('en-AU')}`
                          : 'Select dates')
                      : `${months.find(m => m.value === clientMonth)?.label} ${clientYear}`
                    }
                  </span>
                </li>
                <li>
                  <span className="text-slate-500">Client:</span>{' '}
                  <span className="font-medium">
                    {selectedClientId 
                      ? clients.find(c => c.id === selectedClientId)?.name 
                      : 'Not selected'}
                  </span>
                </li>
                {selectedProjectId && (
                  <li>
                    <span className="text-slate-500">Project:</span>{' '}
                    <span className="font-medium">
                      {jobs.find(j => j.id === selectedProjectId)?.job_number} - {jobs.find(j => j.id === selectedProjectId)?.name}
                    </span>
                  </li>
                )}
                <li>
                  <span className="text-slate-500">Content:</span>{' '}
                  <span className="font-medium">Timesheet details without financial data</span>
                </li>
              </ul>
            </div>

            {/* Download Button */}
            <div className="flex justify-end gap-3">
              <button
                onClick={openEmailModal}
                disabled={!selectedClientId}
                className="btn-secondary flex items-center gap-2 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="email-client-report-btn"
              >
                <Mail className="w-4 h-4" />
                Email to Client
              </button>
              <button
                onClick={handleDownloadClientSummary}
                disabled={downloadingClient || !selectedClientId}
                className="btn-primary bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="generate-client-report-btn"
              >
                {downloadingClient ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Generate Client Summary PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Quick Info */}
        <div className="card-base">
          <div className="card-body">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Report Information</h3>
            <div className="text-sm text-slate-600 space-y-2">
              <p>
                <strong>Monthly Report</strong> includes all approved timesheets for the selected period 
                with financial summary (charged amount, paid amount, margin). This is for internal use only.
              </p>
              <p>
                <strong>Client Summary Report</strong> provides a timesheet summary for a specific client 
                without any financial data (rates, costs, margins). This is suitable for sharing with clients 
                for verification and approval of work performed.
              </p>
              <p>
                <strong>Email to Client</strong> allows you to send the PDF report directly to the client's 
                email address using your configured email templates.
              </p>
              <p>
                <strong>Individual timesheets</strong> can be downloaded as PDF from the timesheet 
                detail page using the "Download PDF" button.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowEmailModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 my-8 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              Email Report to Client
            </h3>
            
            <form onSubmit={handleSendEmail} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Recipient Email *
                </label>
                <input
                  type="email"
                  value={emailForm.recipient_email}
                  onChange={(e) => setEmailForm({ ...emailForm, recipient_email: e.target.value })}
                  required
                  className="input-base"
                  placeholder="client@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Recipient Name
                </label>
                <input
                  type="text"
                  value={emailForm.recipient_name}
                  onChange={(e) => setEmailForm({ ...emailForm, recipient_name: e.target.value })}
                  className="input-base"
                  placeholder="John Smith"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email Template
                </label>
                <select
                  value={emailForm.template_id}
                  onChange={(e) => setEmailForm({ ...emailForm, template_id: e.target.value })}
                  className="input-base"
                >
                  <option value="">-- Use default or custom message --</option>
                  {emailTemplates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} {t.is_default && '(Default)'}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Custom Subject (optional)
                </label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  className="input-base"
                  placeholder="Leave blank to use template subject"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Custom Message (optional)
                </label>
                <textarea
                  value={emailForm.custom_message}
                  onChange={(e) => setEmailForm({ ...emailForm, custom_message: e.target.value })}
                  rows={4}
                  className="input-base resize-none"
                  placeholder="Leave blank to use template. Supports HTML."
                />
              </div>
              
              {/* Additional Attachments */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <Paperclip className="w-4 h-4 inline mr-1" />
                  Additional Attachments
                </label>
                <input
                  ref={emailFileInputRef}
                  type="file"
                  multiple
                  onChange={handleEmailFileSelect}
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt"
                />
                <button
                  type="button"
                  onClick={() => emailFileInputRef.current?.click()}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm"
                >
                  <Upload className="w-4 h-4" />
                  Add Files
                </button>
                {emailAttachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {emailAttachments.map((file, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm">
                        <FileText className="w-4 h-4 text-slate-500" />
                        <span className="flex-1 truncate">{file.name}</span>
                        <span className="text-slate-400">{(file.size / 1024).toFixed(0)} KB</span>
                        <button
                          type="button"
                          onClick={() => removeEmailAttachment(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Note: The main PDF report will be attached automatically.
                </p>
              </div>
              
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">
                  <strong>Report:</strong> {clients.find(c => c.id === selectedClientId)?.name} - {months.find(m => m.value === clientMonth)?.label} {clientYear}
                </p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowEmailModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingEmail}
                  className="btn-primary flex items-center gap-2"
                >
                  {sendingEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Email
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ReportsPage;
